import { useMemo, useRef, useState, type MouseEvent } from 'react'
import type {
  FindingCheckRow,
  FindingListItem,
  FindingsAttentionItem,
  FindingsScope,
  FindingsAttentionScope,
} from '../lib/api.ts'
import { AccountFilter } from '../components/AccountFilter.tsx'
import { DateRange, type DateWindow } from '../components/DateRange.tsx'
import { FindingsAttention } from '../components/findings-report/FindingsAttention.tsx'
import { FindingsAttentionModal } from '../components/findings-report/FindingsAttentionModal.tsx'
import { FindingsByCheck } from '../components/findings-report/FindingsByCheck.tsx'
import { FindingsListModal } from '../components/findings-report/FindingsListModal.tsx'
import { FindingsPath } from '../components/findings-report/FindingsPath.tsx'
import { FindingsHelp } from '../components/findings-report/FindingsHelp.tsx'
import { FINDINGS_MIN_DATE, defaultFindingsWindow } from '../lib/findingsReport.ts'
import { CheckCell, checkRowName, CHECK_CELL_LABEL } from '../lib/findingsList.ts'
import { useFindingsReport } from '../lib/useFindingsReport.ts'

const MIN_DAY = new Date(2026, 9, 1)

export function FindingsReport({
  onOpenVerification,
  onOpenAttentionVerification,
}: {
  onOpenVerification: (item: FindingListItem, event: MouseEvent<HTMLAnchorElement>) => void
  onOpenAttentionVerification: (
    item: FindingsAttentionItem,
    event: MouseEvent<HTMLAnchorElement>,
  ) => void
}) {
  const [range, setRange] = useState<DateWindow>(() => defaultFindingsWindow(new Date()))
  const [account, setAccount] = useState('')
  const [listed, setListed] = useState<
    { row: FindingCheckRow; cell: CheckCell; scope: FindingsScope } | undefined
  >()
  const [attentionScope, setAttentionScope] = useState<FindingsAttentionScope | undefined>()
  const [explored, setExplored] = useState<CheckCell | undefined>()
  const details = useRef<HTMLDivElement>(null)
  const scope = useMemo<FindingsScope>(() => ({ ...range, account, states: [] }), [range, account])
  const { answered, loading, error } = useFindingsReport(scope)
  const report = answered?.report
  const clearValue = defaultFindingsWindow(new Date())

  const openAttention = () => {
    const source = answered?.scope
    if (source !== undefined) {
      setAttentionScope({ from: source.from, to: source.to, account: source.account })
    }
  }

  const openDetail = (row: FindingCheckRow, cell: CheckCell) => {
    if (answered !== undefined) setListed({ row, cell, scope: answered.scope })
  }

  const explore = (cell: CheckCell) => {
    setExplored(cell)
    details.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    details.current?.focus({ preventScroll: true })
  }

  return (
    <div className={`flex flex-col gap-5 ${loading && report !== undefined ? 'opacity-60' : ''}`}>
      <div className="flex flex-wrap items-center gap-3">
        <DateRange
          label="Período"
          value={range}
          onChange={setRange}
          minDate={MIN_DAY}
          clearValue={clearValue}
        />
        <AccountFilter
          value={account}
          onChange={setAccount}
          className="w-full sm:w-auto"
          selectClassName="max-w-full flex-1 sm:flex-none"
        />
        <span className="text-tSmall text-text-weak inline-flex items-center gap-1">
          <FindingsHelp
            label="Recorte do relatório"
            content={{
              calculation:
                'As datas selecionam o dia em que a conferência ficou disponível para o conferente, no horário de São Paulo. As que já aguardavam o conferente no início de 01/10/2026 entram nesse dia; as demais entram no dia da primeira disponibilidade a partir desse corte.',
              source: 'Histórico de disponibilidade para conferência das contas selecionadas.',
              includes:
                'Conferências de dias anteriores que ainda aguardavam o conferente no início de 01/10/2026. Os cartões de utilização registram respostas e conclusão conhecidas no primeiro marco de produção; histórico insuficiente fica indeterminado e fora das taxas. Eventos de feedback e pareceres humanos anteriores a 01/10/2026 ficam fora da triagem deste relatório.',
              excludes:
                'Conferências encerradas antes de 01/10/2026 sem nova entrada na conferência. Simulações em servidores fora de desenvolvimento.',
              provisional:
                'O fluxo e a lista de atenção filtram o dia de entrada na conferência. Os cartões selecionam a primeira ida registrada à produção após a entrada elegível; o período filtra a data dessa primeira ida, e uma ida posterior não cria outra primeira ida. O backlog de abertura pertence a 01/10 e não é reincluído em cada dia posterior; uma nova passagem pela conferência não altera a primeira entrada atribuída.',
            }}
          >
            Operação a partir de {FINDINGS_MIN_DATE.split('-').reverse().join('/')}
          </FindingsHelp>
        </span>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-text-brand text-[10px] font-semibold uppercase tracking-[0.16em]">
            Comparação · Respostas · Triagem
          </p>
          <h1 className="text-text-strong mt-1 text-2xl font-semibold tracking-tight">
            Qualidade dos apontamentos
          </h1>
        </div>
        <span className="text-tSmall text-text-weak inline-flex items-center gap-2">
          <FindingsHelp
            label="Premissas do painel"
            content={{
              calculation: 'Divergências → respostas registradas → contestações triadas.',
              source: 'Resultados atuais, histórico de decisões e triagem humana.',
              includes:
                'Concordâncias operacionais contam como respostas. Apenas contestações triadas confirmam acerto ou erro.',
              excludes:
                'Não avaliados, informativos e linhas sem divergência ficam fora dos totais novos. Sem resposta nunca é acerto ou erro.',
              provisional:
                'Não há telemetria para confirmar se cada um foi visualizado. Uma resposta provisória pode mudar até Concluir.',
            }}
          >
            Acerto e erro exigem triagem
          </FindingsHelp>
        </span>
      </div>

      {error !== undefined && (
        <p role="alert" className="text-tP text-text-error">
          {error}
        </p>
      )}
      {report === undefined ? (
        error === undefined && <p className="text-tP text-text-weak">Carregando…</p>
      ) : (
        <>
          <FindingsPath report={report} selected={explored} onExplore={explore} />
          <FindingsAttention report={report} onOpen={openAttention} />
          <div
            ref={details}
            tabIndex={-1}
            aria-label="Origem por verificação"
            className="scroll-mt-4 rounded-2xl"
          >
            <FindingsByCheck
              rows={report.byCheck}
              onOpenCell={openDetail}
              selectedCell={explored}
              onClear={() => setExplored(undefined)}
            />
          </div>
        </>
      )}

      {listed !== undefined && (
        <FindingsListModal
          title={`${checkRowName(listed.row)} · ${CHECK_CELL_LABEL[listed.cell]}`}
          scope={listed.scope}
          cell={{
            check: listed.row.check,
            rule: listed.row.rule,
            cell: listed.cell,
            basis: 'answered',
          }}
          onOpen={onOpenVerification}
          onClose={() => setListed(undefined)}
        />
      )}
      {attentionScope !== undefined && (
        <FindingsAttentionModal
          scope={attentionScope}
          onOpen={onOpenAttentionVerification}
          onClose={() => setAttentionScope(undefined)}
        />
      )}
    </div>
  )
}
